import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import moment from "moment";

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState(null);
  
  // Ref para manter referência dos listeners ativos
  const listenersRef = useRef(new Set());
  
  // Ref para controlar se já está fazendo refresh (evita múltiplas tentativas)
  const isRefreshing = useRef(false);
  const failedQueue = useRef([]);

  // Interceptors do API (mantém como estava)
  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
        setIsAuth(true);
      }
      return config;
    },
    (error) => {
      Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      
      // Tratamento para erro 403 (token expirado, tenta refresh)
      if (error?.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true;

        // Se já está fazendo refresh, adiciona à fila
        if (isRefreshing.current) {
          return new Promise((resolve, reject) => {
            failedQueue.current.push({ resolve, reject });
          }).then(() => {
            return api(originalRequest);
          }).catch((err) => {
            return Promise.reject(err);
          });
        }

        isRefreshing.current = true;

        try {
          // Remove o token antigo do header para evitar usar token inválido
          delete originalRequest.headers["Authorization"];
          
          const { data } = await api.post("/auth/refresh_token");
          if (data && data.token) {
            localStorage.setItem("token", JSON.stringify(data.token));
            api.defaults.headers.Authorization = `Bearer ${data.token}`;
            
            // Atualiza o header da requisição original
            originalRequest.headers["Authorization"] = `Bearer ${data.token}`;
            
            // Processa a fila de requisições pendentes
            failedQueue.current.forEach(({ resolve }) => resolve());
            failedQueue.current = [];
            isRefreshing.current = false;
            
            return api(originalRequest);
          } else {
            throw new Error("Token não retornado no refresh");
          }
        } catch (refreshError) {
          // Se o refresh falhar, limpa tudo e redireciona
          failedQueue.current.forEach(({ reject }) => reject(refreshError));
          failedQueue.current = [];
          isRefreshing.current = false;
          
          localStorage.removeItem("token");
          localStorage.removeItem("cshow");
          localStorage.removeItem("companyDueDate");
          localStorage.removeItem("profileImage");
          api.defaults.headers.Authorization = undefined;
          setIsAuth(false);
          
          // Evita redirecionar se já estiver na página de login ou se for uma requisição de autenticação
          const isAuthEndpoint = originalRequest?.url?.includes("/auth/login") || 
                                 originalRequest?.url?.includes("/auth/refresh_token") ||
                                 originalRequest?.url?.includes("/auth/signup");
          const isLoginPage = window.location.pathname === "/login" || 
                             window.location.pathname === "/signup";
          
          if (!isAuthEndpoint && !isLoginPage) {
            history.push("/login");
          }
          
          // Rejeita silenciosamente para não mostrar erro na tela
          return Promise.reject(new Error("Sessão expirada"));
        }
      }
      
      // Tratamento para erro 401 (não autorizado)
      if (error?.response?.status === 401) {
        // Ignora erro 401 na requisição de logout (é esperado quando fazemos logout)
        const isLogoutEndpoint = originalRequest?.url?.includes("/auth/logout");
        
        if (!isLogoutEndpoint) {
          localStorage.removeItem("token");
          localStorage.removeItem("cshow");
          localStorage.removeItem("companyDueDate");
          localStorage.removeItem("profileImage");
          api.defaults.headers.Authorization = undefined;
          setIsAuth(false);
          
          // Evita redirecionar se já estiver na página de login ou se for uma requisição de autenticação
          const isAuthEndpoint = originalRequest?.url?.includes("/auth/login") || 
                                 originalRequest?.url?.includes("/auth/refresh_token") ||
                                 originalRequest?.url?.includes("/auth/signup");
          const isLoginPage = window.location.pathname === "/login" || 
                             window.location.pathname === "/signup";
          
          if (!isAuthEndpoint && !isLoginPage) {
            // Redireciona para login
            history.push("/login");
          }
        }
        // Se for logout, apenas rejeita silenciosamente (não mostra erro)
      }
      
      return Promise.reject(error);
    }
  );

  // Effect para inicialização do token
  useEffect(() => {
    const token = localStorage.getItem("token");
    (async () => {
      if (token) {
        try {
          const { data } = await api.post("/auth/refresh_token");
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          setIsAuth(true);
          setUser(data.user || data);
        } catch (err) {
          toastError(err);
        }
      }
      setLoading(false);
    })();
  }, []);

  // Effect para configuração do socket
  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      console.log("Configurando socket para user", user.id, "company", user.companyId);
      
      // Limpar listeners anteriores
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }

      // Criar nova conexão socket
      const socketInstance = socketConnection({ user: {
        companyId: user.companyId,
        id: user.id }
      });
      
      if (socketInstance) {
        setSocket(socketInstance);

        // Aguardar um pouco para garantir que o socket está configurado
        setTimeout(() => {
          const eventName = `company-${user.companyId}-user`;
          
          const handleUserUpdate = (data) => {
            if (data.action === "update" && data.user.id === user.id) {
              setUser(data.user);
            }
          };

          // Verificar se o socket tem o método 'on'
          if (socketInstance && typeof socketInstance.on === 'function') {
            socketInstance.on(eventName, handleUserUpdate);
            listenersRef.current.add(eventName);
            console.log(`Listener adicionado para: ${eventName}`);
          } else {
            console.error("Socket instance não tem método 'on'", socketInstance);
          }
        }, 100);
      }
    }

    // Cleanup function
    return () => {
      if (socket && listenersRef.current.size > 0) {
        console.log("Limpando listeners do socket para user", user.id);
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }
    };
  }, [user.id, user.companyId]); // Dependências específicas

  // Effect para buscar dados do usuário atual
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user || data);
      } catch (err) {
        console.log("Erro ao buscar usuário atual:", err);
      }
    };
    
    if (isAuth) {
      fetchCurrentUser();
    }
  }, [isAuth]);

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);
      const {
        user: { company },
      } = data;

      // Lógica de configurações da empresa (mantém como estava)
      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        const signEnable = setting.value === "enable";

        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable);
        }
      }
      
      localStorage.setItem("profileImage", data.user.profileImage);

      moment.locale("pt-br");
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = "2999-12-31T00:00:00.000Z";
      } else {
        dueDate = data.user.company.dueDate;
      }
      
      const hoje = moment(moment()).format("DD/MM/yyyy");
      const vencimento = moment(dueDate).format("DD/MM/yyyy");

      // Comparar apenas as datas (sem horas) para permitir acesso até 23h59 do dia do vencimento
      const hojeInicio = moment().startOf('day');
      const vencimentoInicio = moment(dueDate).startOf('day');
      
      var diff = vencimentoInicio.diff(hojeInicio, 'days');
      var before = hojeInicio.isSameOrBefore(vencimentoInicio, 'day');
      var dias = diff;

      if (before === true) {
        localStorage.setItem("token", JSON.stringify(data.token));
        localStorage.setItem("companyDueDate", vencimento);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user || data);
        setIsAuth(true);
        toast.success(i18n.t("auth.toasts.success"));
        
        if (Math.round(dias) < 5) {
          toast.warn(
            `Sua assinatura vence em ${Math.round(dias)} ${
              Math.round(dias) === 1 ? "dia" : "dias"
            } `
          );
        }

        const mobileRedirect = localStorage.getItem("mobileRedirect");
        if (mobileRedirect === "true") {
          localStorage.removeItem("mobileRedirect");
          history.push("/mobile/tickets");
        } else {
          history.push("/tickets");
        }
        setLoading(false);
      } else {
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setIsAuth(true);
        toastError(`Opss! Sua assinatura venceu ${vencimento}.
Entre em contato com o Suporte para mais informações! `);
        history.push("/financeiro-aberto");
        setLoading(false);
      }
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      // Limpar socket antes do logout
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
        
        if (typeof socket.disconnect === 'function') {
          socket.disconnect();
        }
      }

      // Tenta fazer logout no servidor, mas não mostra erro se falhar (pode ser 401 esperado)
      try {
        await api.delete("/auth/logout");
      } catch (logoutError) {
        // Ignora erros de logout (pode ser 401 se o token já foi invalidado)
        // Não mostra erro para o usuário
        console.log("Logout request completed (may have failed silently)");
      }

      // Limpa estado local independente do resultado da requisição
      setIsAuth(false);
      setUser({});
      setSocket(null);
      localStorage.removeItem("token");
      localStorage.removeItem("cshow");
      localStorage.removeItem("companyDueDate");
      localStorage.removeItem("profileImage");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    } catch (err) {
      // Em caso de erro inesperado, ainda limpa o estado e redireciona
      setIsAuth(false);
      setUser({});
      setSocket(null);
      localStorage.removeItem("token");
      localStorage.removeItem("cshow");
      localStorage.removeItem("companyDueDate");
      localStorage.removeItem("profileImage");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      console.log(data);
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket,
  };
};

export default useAuth;